/**
 * Ein Diagramm mit mehreren Messreihen (W12).
 *
 * Alle ausgewaehlten Reihen liegen uebereinander auf einer gemeinsamen Achse.
 * Nur so lassen sie sich vergleichen - und beim Reglereinstellen ist genau der
 * Vergleich die Aufgabe: der senkrechte Abstand zwischen Soll- und Ist-Linie
 * ist die Regelabweichung.
 *
 * Reihen mit sehr verschiedenen Wertebereichen lassen sich ueber die Legende
 * einzeln abschalten. Das ist ehrlicher als eine zweite Achse, bei der zwei
 * Kurven gleich aussehen, obwohl sie es nicht sind.
 */

import { Text } from "@microbit/ui";
import { useIntl } from "react-intl";
import { Box } from "styled-system/jsx";
import {
  colourFor,
  combinedBounds,
  formatValue,
  niceTicks,
  polylines,
} from "./chart";
import type { Channel } from "./series";

/** Breite der Zeichenflaeche in eigenen Koordinaten. */
const WIDTH = 300;

/** Platz links fuer die Werteachse, unten fuer die Zeitachse. */
const AXIS_LEFT = 40;
const AXIS_BOTTOM = 16;

interface ChartProps {
  channels: Channel[];
  times: number[];
  height: number;
}

const Chart = ({ channels, times, height }: ChartProps) => {
  const intl = useIntl();
  const plotHeight = height - AXIS_BOTTOM;
  const range = combinedBounds(channels);
  const spanne = times.length > 1 ? times[times.length - 1] - times[0] : 0;

  if (!range || times.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
        height={`${height}px`}
      />
    );
  }

  const y = (wert: number) =>
    plotHeight - ((wert - range.min) / (range.max - range.min)) * plotHeight;

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white">
      <svg
        viewBox={`0 0 ${WIDTH + AXIS_LEFT} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={intl.formatMessage({ id: "telemetry-chart-alt" })}
      >
        {/* Gitter und Werteachse */}
        {niceTicks(range).map((wert) => (
          <g key={`y-${wert}`}>
            <line
              x1={AXIS_LEFT}
              x2={WIDTH + AXIS_LEFT}
              y1={y(wert)}
              y2={y(wert)}
              stroke="#eef2f6"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={AXIS_LEFT - 5}
              y={Math.min(Math.max(y(wert), 8), plotHeight)}
              textAnchor="end"
              fontSize={9}
              fill="#64748b"
            >
              {formatValue(wert)}
            </text>
          </g>
        ))}

        {/* Achsenlinien */}
        <line
          x1={AXIS_LEFT}
          x2={AXIS_LEFT}
          y1={0}
          y2={plotHeight}
          stroke="#cbd5e1"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={AXIS_LEFT}
          x2={WIDTH + AXIS_LEFT}
          y1={plotHeight}
          y2={plotHeight}
          stroke="#cbd5e1"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />

        {/* Zeitachse: Anfang und Ende genuegen, die Spanne steht daneben. */}
        <text x={AXIS_LEFT} y={height - 3} fontSize={9} fill="#64748b">
          0 s
        </text>
        <text
          x={WIDTH + AXIS_LEFT}
          y={height - 3}
          textAnchor="end"
          fontSize={9}
          fill="#64748b"
        >
          {spanne.toFixed(1)} s
        </text>

        {/* Die Linien, zuletzt gezeichnet und damit oben */}
        <g transform={`translate(${AXIS_LEFT} 0)`}>
          {channels.map((channel, index) =>
            polylines(
              channel,
              times,
              { width: WIDTH, height: plotHeight },
              range
            ).map((punkte) => (
              <polyline
                key={`${channel.name}-${punkte.slice(0, 16)}`}
                points={punkte}
                fill="none"
                stroke={colourFor(index)}
                strokeWidth={1.6}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ))
          )}
        </g>
      </svg>
    </Box>
  );
};

export default Chart;

interface LegendProps {
  channels: Channel[];
  hidden: Set<string>;
  onToggle: (name: string) => void;
}

/** Legende mit Ein- und Ausschalter je Reihe. */
export const Legend = ({ channels, hidden, onToggle }: LegendProps) => (
  <Box display="flex" flexWrap="wrap" gap="1.5" pt="1">
    {channels.map((channel, index) => {
      const aus = hidden.has(channel.name);
      const letzter = [...channel.values].reverse().find((v) => v !== null);
      return (
        <button
          key={channel.name}
          type="button"
          onClick={() => onToggle(channel.name)}
          aria-pressed={!aus}
          data-testid={`legend-${channel.name}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "1px 6px",
            borderRadius: "3px",
            border: `1px solid ${aus ? "#e2e8f0" : "#cbd5e1"}`,
            background: aus ? "#f8fafc" : "#ffffff",
            opacity: aus ? 0.5 : 1,
            cursor: "pointer",
          }}
        >
          <Box
            width="8px"
            height="8px"
            borderRadius="full"
            bg={colourFor(index)}
            flexShrink="0"
          />
          <Text fontSize="2xs">{channel.name}</Text>
          <Text fontSize="2xs" fontFamily="code" color="fg.muted">
            {letzter === undefined || letzter === null
              ? "—"
              : formatValue(letzter)}
          </Text>
        </button>
      );
    })}
  </Box>
);
